/**
 * Which organisation (workspace) this tab works in (ADR 0113).
 *
 * The first segment of every signed-in URL is the tenant slug, so the URL
 * decides: a shared link opens in the team it belongs to, and two tabs can
 * work in two teams. `/` and `/app` use the workspace the person used last.
 * Switching saves the choice and loads the other team's URL, which also starts
 * the caches and sockets afresh.
 *
 * Kept free of app imports: the entry script reads it before the app loads.
 */
import {
  getWorkspaceTenantId,
  onWorkspaceUnavailable,
  setWorkspaceTenantId,
  workspaceForSlug,
  type Workspace,
} from '@rudi/api/workspace';

const TOKEN_STORAGE_KEY = 'rudi_auth_tokens';
const LAST_WORKSPACE_PREFIX = 'rudi:workspace:v1:';

interface StoredUser {
  id: string;
  tenantId: string | null;
  workspaces: Workspace[];
}

function storedUser(storage: Storage): StoredUser | null {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(TOKEN_STORAGE_KEY) ?? 'null');
    const user = (parsed as { user?: unknown } | null)?.user as
      { id?: unknown; tenantId?: unknown; workspaces?: unknown } | null | undefined;
    if (!user || user.id == null) return null;
    return {
      id: String(user.id),
      tenantId: typeof user.tenantId === 'string' ? user.tenantId : null,
      workspaces: Array.isArray(user.workspaces) ? (user.workspaces as Workspace[]) : [],
    };
  } catch {
    return null;
  }
}

function lastWorkspaceKey(userId: string): string {
  return `${LAST_WORKSPACE_PREFIX}${userId}`;
}

/** The workspace `userId` used last, if they chose one. */
export function lastWorkspaceTenantId(
  userId: string,
  storage: Storage = window.localStorage
): string | null {
  try {
    return storage.getItem(lastWorkspaceKey(userId));
  } catch {
    return null;
  }
}

export function rememberWorkspace(
  userId: string,
  tenantId: string | null,
  storage: Storage = window.localStorage
): void {
  try {
    if (tenantId) storage.setItem(lastWorkspaceKey(userId), tenantId);
    else storage.removeItem(lastWorkspaceKey(userId));
  } catch {
    // Private mode: the choice lasts as long as the tab.
  }
}

/**
 * The tenant a page load at `pathname` works in: the workspace its slug names,
 * else the one used last, else none (the backend's default).
 */
export function workspaceTenantIdForPath(
  pathname: string,
  storage: Storage = window.localStorage
): string | null {
  const user = storedUser(storage);
  if (!user) return null;
  const [slug] = pathname.split('/').filter(Boolean);
  const named = workspaceForSlug(user.workspaces, slug);
  if (named) return named.tenantId;
  const last = lastWorkspaceTenantId(user.id, storage);
  if (!last) return null;
  // A workspace the person left is forgotten; one the stored user does not
  // list yet (an older payload) is still tried, and the backend decides.
  if (user.workspaces.length > 0 && !user.workspaces.some(ws => ws.tenantId === last)) {
    return null;
  }
  return last;
}

/** Whether `slug` names one of the stored user's workspaces. */
export function storedWorkspaceSlug(slug: string | undefined, storage: Storage): boolean {
  return !!workspaceForSlug(storedUser(storage)?.workspaces, slug);
}

/**
 * Pick this tab's workspace before the first request. Called once by the
 * application root; a refused workspace falls back to the default one.
 */
export function installWorkspaceSelection(): void {
  setWorkspaceTenantId(workspaceTenantIdForPath(window.location.pathname));
  onWorkspaceUnavailable(tenantId => {
    const user = storedUser(window.localStorage);
    if (user && lastWorkspaceTenantId(user.id) === tenantId) rememberWorkspace(user.id, null);
    if (getWorkspaceTenantId() !== tenantId) return;
    setWorkspaceTenantId(null);
    window.location.assign('/');
  });
}

/** The URL of `path` (such as `home` or `team-chat`) in `workspace`. */
export function workspaceHref(workspace: Workspace, userId: string, path = 'home'): string {
  return `/${workspace.tenantSlug}/${userId}/${path.replace(/^\/+/, '')}`;
}

/** Work in `workspace` from now on, and open `path` there. */
export function switchWorkspace(workspace: Workspace, userId: string, path = 'home'): void {
  rememberWorkspace(userId, workspace.tenantId);
  setWorkspaceTenantId(workspace.tenantId);
  window.location.assign(workspaceHref(workspace, userId, path));
}

/**
 * Load `href` in the workspace its slug names when that is not this tab's,
 * so the URL and the requests always agree. Returns whether it did.
 */
export function enterWorkspaceOf(
  href: string,
  load: (href: string) => void = url => window.location.assign(url)
): boolean {
  const user = storedUser(window.localStorage);
  if (!user) return false;
  const [slug] = href.split('?')[0].split('/').filter(Boolean);
  const named = workspaceForSlug(user.workspaces, slug);
  const current = getWorkspaceTenantId() ?? user.tenantId;
  if (!named || named.tenantId === current) return false;
  rememberWorkspace(user.id, named.tenantId);
  setWorkspaceTenantId(named.tenantId);
  load(href);
  return true;
}
